import "./KanbanColumn.css";

import React, { memo, useCallback, useLayoutEffect, useRef } from "react";

import { TaskCard } from "../../../../TaskCard";
import { COMPLETED_COLUMN_ID, UNASSIGNED_COLUMN_ID } from "../../constants";
import { KanbanColumnId, KanbanColumnModel, KanbanTask, TasksDashboardVariant } from "../../models";

const LOADING_CARD_COUNT = 3;
const DEFAULT_PLACEHOLDER_HEIGHT = 120;

interface KanbanColumnProps {
  column: KanbanColumnModel;
  variant: TasksDashboardVariant;
  isLoading: boolean;
  draggingIds: string[];
  dropIndex: number | null;
  placeholderHeight: number | null;
  selectedIds: Set<string>;
  onCardDragStart: (taskId: string, height: number) => void;
  onCardDragEnd: () => void;
  onDragOverColumn: (columnId: KanbanColumnId, index: number) => void;
  onDragLeaveColumn: (columnId: KanbanColumnId) => void;
  onDropColumn: (columnId: KanbanColumnId) => void;
  resolveDragCount: (taskId: string) => number;
  onToggleSelect: (taskId: string) => void;
  onClearSelection: () => void;
  onTaskClick?: (taskId: string) => void;
  onObjectClick?: (taskId: string) => void;
  /** Fired by "Show more" when the lane is paged and the server holds further cards. */
  onShowMore?: (columnId: KanbanColumnId) => void;
}

const DropSlot = ({ height }: { height: number | null }): JSX.Element => (
  <div className="KanbanColumn_dropSlot" style={{ height: height ?? DEFAULT_PLACEHOLDER_HEIGHT }} aria-hidden />
);

const MAX_DECK_BACK_CARDS = 2;

interface DragImage {
  element: HTMLElement;
  offsetX: number;
  offsetY: number;
}

const BADGE_HALF = 11;

/**
 * Build the drag image. The real card(s) are lifted out of the column and the browser can't
 * restyle its auto-generated drag image, so we render an off-screen styled clone and hand it to
 * `setDragImage`. For a multi-select drag we fan a couple of blank cards behind the top card so it
 * reads as a spread deck, with a count badge centred on the top-right corner. Returns the element
 * plus the cursor offset (adjusted for any headroom the badge needs above the top card).
 */
const createDragImage = (
  node: HTMLElement,
  count: number,
  variant: TasksDashboardVariant,
  pointerX: number,
  pointerY: number
): DragImage => {
  const rect = node.getBoundingClientRect();
  const radius = variant === "hub" ? "0" : "12px";
  const clone = node.cloneNode(true) as HTMLElement;
  clone.classList.add("TaskCard_dragging");
  clone.style.pointerEvents = "none";
  clone.style.margin = "0";
  clone.style.width = `${rect.width}px`;

  if (count <= 1) {
    clone.style.position = "absolute";
    clone.style.top = "-9999px";
    clone.style.left = "-9999px";
    document.body.appendChild(clone);
    // The browser copies the image synchronously in setDragImage, so it's safe to remove next tick.
    window.setTimeout(() => clone.remove(), 0);
    return { element: clone, offsetX: pointerX, offsetY: pointerY };
  }

  const backCards = Math.min(count - 1, MAX_DECK_BACK_CARDS);
  const spread = 8;
  // Top card sits BADGE_HALF from the top so the corner badge can straddle its top edge.
  const wrapper = document.createElement("div");
  wrapper.style.position = "absolute";
  wrapper.style.top = "-9999px";
  wrapper.style.left = "-9999px";
  wrapper.style.width = `${rect.width + backCards * spread + BADGE_HALF + 8}px`;
  wrapper.style.height = `${rect.height + backCards * spread + BADGE_HALF + 8}px`;
  wrapper.style.pointerEvents = "none";

  for (let i = backCards; i >= 1; i -= 1) {
    const back = document.createElement("div");
    back.style.position = "absolute";
    back.style.left = `${i * spread}px`;
    back.style.top = `${BADGE_HALF + i * spread}px`;
    back.style.width = `${rect.width}px`;
    back.style.height = `${rect.height}px`;
    back.style.background = "#fff";
    back.style.border = "1px solid rgba(44, 48, 59, 0.2)";
    back.style.borderRadius = radius;
    back.style.boxShadow = "0px 2px 4px rgba(44, 48, 59, 0.06)";
    back.style.transform = `rotate(${i * 2}deg)`;
    back.style.transformOrigin = "top left";
    wrapper.appendChild(back);
  }

  clone.style.position = "absolute";
  clone.style.left = "0";
  clone.style.top = `${BADGE_HALF}px`;
  wrapper.appendChild(clone);

  // "N selected" count badge, centred on the top-right corner of the top card.
  const badge = document.createElement("div");
  badge.textContent = String(count);
  badge.style.position = "absolute";
  badge.style.top = `${BADGE_HALF}px`;
  badge.style.left = `${rect.width}px`;
  badge.style.transform = "translate(-50%, -50%)";
  badge.style.minWidth = "22px";
  badge.style.height = "22px";
  badge.style.padding = "0 6px";
  badge.style.boxSizing = "border-box";
  badge.style.display = "flex";
  badge.style.alignItems = "center";
  badge.style.justifyContent = "center";
  badge.style.borderRadius = "999px";
  badge.style.background = "#7262d9";
  badge.style.color = "#fff";
  badge.style.fontFamily = variant === "hub" ? "var(--hub-font-family)" : "'Figtree', sans-serif";
  badge.style.fontSize = "12px";
  badge.style.fontWeight = "700";
  badge.style.boxShadow = "0px 2px 6px rgba(44, 48, 59, 0.2)";
  wrapper.appendChild(badge);

  document.body.appendChild(wrapper);
  window.setTimeout(() => wrapper.remove(), 0);
  return { element: wrapper, offsetX: pointerX, offsetY: pointerY + BADGE_HALF };
};

// ─── Card ─────────────────────────────────────────────────────────────────────

interface KanbanCardProps {
  task: KanbanTask;
  variant: TasksDashboardVariant;
  isCompletedColumn: boolean;
  draggable: boolean;
  onCardDragStart: (taskId: string, height: number) => void;
  onCardDragEnd: () => void;
  resolveDragCount: (taskId: string) => number;
  onTaskClick?: (taskId: string) => void;
  onObjectClick?: (taskId: string) => void;
}

// Memoised so that drag moves (which re-render the hovered column to reposition the drop slot)
// don't re-render every card — only the slot moves. Requires stable handler identities.
const KanbanCard = memo(
  ({
    task,
    variant,
    isCompletedColumn,
    draggable,
    onCardDragStart,
    onCardDragEnd,
    resolveDragCount,
    onTaskClick,
    onObjectClick,
  }: KanbanCardProps): JSX.Element => {
    // Completed cards always render in the default state — the overdue treatment is suppressed.
    const state = !isCompletedColumn && task.isOverdue ? "overdue" : "default";

    return (
      <TaskCard
        variant={variant}
        mode="kanban"
        state={state}
        draggable={draggable}
        onDragStart={
          draggable
            ? (e) => {
                const node = e.currentTarget;
                const rect = node.getBoundingClientRect();
                e.dataTransfer.effectAllowed = "move";
                // Some browsers (e.g. Firefox) won't start a drag unless data is set.
                e.dataTransfer.setData("text/plain", task.id);
                // Violet-bordered drag image (spread deck + count badge for a multi-selection).
                const { element, offsetX, offsetY } = createDragImage(
                  node,
                  resolveDragCount(task.id),
                  variant,
                  e.clientX - rect.left,
                  e.clientY - rect.top
                );
                e.dataTransfer.setDragImage(element, offsetX, offsetY);
                onCardDragStart(task.id, rect.height);
              }
            : undefined
        }
        onDragEnd={draggable ? onCardDragEnd : undefined}
        title={task.title}
        linkedObjectName={task.linkedObjectName}
        linkedObjectType={task.linkedObjectType}
        assigneeInitials={task.assigneeInitials || "?"}
        assigneeName={task.assigneeName || "Unassigned"}
        dueDate={task.dueDate}
        daysOverdue={task.daysOverdue}
        isMandatory={task.isMandatory}
        isMilestone={task.isMilestone}
        hasWorkflow={task.hasWorkflow}
        isGeneral={task.isGeneral}
        tags={task.tags}
        onClick={() => onTaskClick?.(task.id)}
        onObjectClick={() => onObjectClick?.(task.id)}
      />
    );
  }
);
KanbanCard.displayName = "KanbanCard";

// ─── Column ─────────────────────────────────────────────────────────────────

const KanbanColumnComponent = ({
  column,
  variant,
  isLoading,
  draggingIds,
  dropIndex,
  placeholderHeight,
  selectedIds,
  onCardDragStart,
  onCardDragEnd,
  onDragOverColumn,
  onDragLeaveColumn,
  onDropColumn,
  resolveDragCount,
  onToggleSelect,
  onClearSelection,
  onTaskClick,
  onObjectClick,
  onShowMore,
}: KanbanColumnProps): JSX.Element => {
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const cardsContainerRef = useRef<HTMLDivElement | null>(null);
  // Cached card midpoints for the current drag pass, so we don't measure the DOM on every
  // dragover event. Recomputed when the drag first enters, when the visible card count changes, or
  // when the drop slot moves — rendering the slot at a new index shifts every card below it, so the
  // cached midpoints would otherwise be stale and the placeholder could settle on the wrong card.
  const geometryRef = useRef<{ count: number; dropIndex: number | null; midpoints: number[] } | null>(null);
  const isDragging = draggingIds.length > 0;
  const isCompletedColumn = column.id === COMPLETED_COLUMN_ID;
  // Unassigned is derived from having no assignee, so its cards aren't draggable/selectable and it
  // isn't a drop target — (un)assignment happens elsewhere and the lane updates to match.
  const isUnassignedColumn = column.id === UNASSIGNED_COLUMN_ID;

  // Soft-fade the list edges, but only towards a side that actually has more content to scroll
  // to — so short columns and the scrolled extremes don't get a stray fade. Toggled directly on
  // the DOM (no re-render) to stay off the drag/render hot path.
  const updateScrollFade = useCallback((): void => {
    const el = cardsContainerRef.current;
    if (!el) return;
    const hasMoreAbove = el.scrollTop > 1;
    const hasMoreBelow = el.scrollHeight - el.scrollTop - el.clientHeight > 1;
    el.classList.toggle("KanbanColumn_cards_fadeTop", hasMoreAbove);
    el.classList.toggle("KanbanColumn_cards_fadeBottom", hasMoreBelow);
  }, []);

  useLayoutEffect(() => {
    const el = cardsContainerRef.current;
    if (!el) return undefined;
    updateScrollFade();
    el.addEventListener("scroll", updateScrollFade, { passive: true });
    const observer = new ResizeObserver(updateScrollFade);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollFade);
      observer.disconnect();
    };
  }, [updateScrollFade]);

  // Re-evaluate after every render so adding/removing cards (which changes scrollHeight without
  // firing scroll/resize) keeps the fade in sync.
  useLayoutEffect(updateScrollFade);

  // Insertion index = number of (visible) cards whose vertical midpoint sits above the pointer.
  const computeDropIndex = (clientY: number): number => {
    const cards = cardRefs.current.filter((el): el is HTMLDivElement => el != null);
    const cache = geometryRef.current;
    const geometry =
      cache && cache.count === cards.length && cache.dropIndex === dropIndex
        ? cache
        : {
            count: cards.length,
            dropIndex,
            midpoints: cards.map((el) => {
              const rect = el.getBoundingClientRect();
              return rect.top + rect.height / 2;
            }),
          };
    geometryRef.current = geometry;
    return geometry.midpoints.reduce((index, mid) => (clientY > mid ? index + 1 : index), 0);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
    // Not draggable-into: leaving the event un-prevented means the browser rejects the drop here.
    if (!isDragging || isUnassignedColumn) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    onDragOverColumn(column.id, computeDropIndex(e.clientY));
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
    // Only clear when the pointer actually leaves the container, not when moving over a child.
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    geometryRef.current = null;
    onDragLeaveColumn(column.id);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    if (isUnassignedColumn) return;
    e.preventDefault();
    geometryRef.current = null;
    onDropColumn(column.id);
  };

  const renderCard = (task: KanbanTask): JSX.Element => (
    <KanbanCard
      task={task}
      variant={variant}
      isCompletedColumn={isCompletedColumn}
      draggable={!isUnassignedColumn}
      onCardDragStart={onCardDragStart}
      onCardDragEnd={onCardDragEnd}
      resolveDragCount={resolveDragCount}
      onTaskClick={onTaskClick}
      onObjectClick={onObjectClick}
    />
  );

  // Shift / ⌘ / Ctrl-click toggles multi-select (intercepted before the card's own click so it
  // doesn't also open the task); a plain click clears any selection and opens the task as usual.
  const handleCardClickCapture = (taskId: string, e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      e.preventDefault();
      e.stopPropagation();
      onToggleSelect(taskId);
    } else {
      onClearSelection();
    }
  };

  const renderCards = (): JSX.Element[] => {
    cardRefs.current = [];
    const items: JSX.Element[] = [];
    let visibleIndex = 0;

    column.tasks.forEach((task) => {
      // A card being dragged is "picked up": kept mounted (so dragend still fires) but removed
      // from the column flow, so it no longer occupies its old slot. In a multi-select drag every
      // selected card is lifted together.
      if (draggingIds.includes(task.id)) {
        items.push(
          <div key={task.id} className="KanbanColumn_liftedCard" aria-hidden>
            {renderCard(task)}
          </div>
        );
        return;
      }

      // Stable key so the slot is repositioned (not remounted) as it moves — avoids re-animating.
      if (dropIndex === visibleIndex) {
        items.push(<DropSlot key="dropslot" height={placeholderHeight} />);
      }

      const refIndex = visibleIndex;
      const isSelected = !isUnassignedColumn && selectedIds.has(task.id);
      items.push(
        <div
          key={task.id}
          className={`KanbanColumn_cardWrap${isSelected ? " KanbanColumn_cardWrap_selected" : ""}`}
          ref={(el) => {
            cardRefs.current[refIndex] = el;
          }}
          onClickCapture={isUnassignedColumn ? undefined : (e) => handleCardClickCapture(task.id, e)}
        >
          {renderCard(task)}
        </div>
      );
      visibleIndex += 1;
    });

    if (dropIndex === visibleIndex) {
      items.push(<DropSlot key="dropslot" height={placeholderHeight} />);
    }

    return items;
  };

  return (
    <section className={`KanbanColumn KanbanColumn_${variant}`} aria-label={column.label}>
      <header className="KanbanColumn_header">
        <span className="KanbanColumn_title">{column.label}</span>
        {/* The lane's real size when the host pages the cards, otherwise the cards on screen. */}
        <span className="KanbanColumn_count">{column.totalCount ?? column.tasks.length}</span>
      </header>

      <div
        className="KanbanColumn_cards"
        ref={cardsContainerRef}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isLoading
          ? Array.from({ length: LOADING_CARD_COUNT }).map((_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`loading-${i}`} className="KanbanColumn_cardWrap">
                <TaskCard variant={variant} mode="kanban" state="loading" />
              </div>
            ))
          : renderCards()}

        {/* Paged lane with further cards on the server — "show more" loads the next page (~20). */}
        {!isLoading && column.hasMoreTasks && (
          <button
            type="button"
            className="KanbanColumn_showMore body3"
            disabled={column.isLoadingTasks}
            onClick={() => onShowMore?.(column.id)}
          >
            {column.isLoadingTasks
              ? "Loading…"
              : `Show more${column.totalCount ? ` (${column.totalCount - column.tasks.length} remaining)` : ""}`}
          </button>
        )}
      </div>
    </section>
  );
};

export const KanbanColumn = memo(KanbanColumnComponent);
